// src/components/ui/DecorQuoteModal.jsx

import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';
import { Checkbox } from './Checkbox';

// Form Options
const eventTypeOptions = [
    { value: 'wedding', label: 'Wedding' },
    { value: 'corporate-gala', label: 'Corporate Gala / Dinner' },
    { value: 'private-celebration', label: 'Private Celebration (Birthday, Anniversary)' },
    { value: 'product-launch', label: 'Product Launch / Brand Event' },
    { value: 'other', label: 'Other' },
];

const stylePreferences = [
    { id: 'modern-minimalist', label: 'Modern & Minimalist' },
    { id: 'classic-elegant', label: 'Classic & Elegant' },
    { id: 'bohemian-rustic', label: 'Bohemian & Rustic' },
    { id: 'luxury-glamorous', label: 'Luxury & Glamorous' },
    { id: 'custom-themed', label: 'Custom Themed' },
];

const decorElements = [
    { id: 'floral-design', label: 'Bespoke Floral Design' },
    { id: 'lighting-design', label: 'Lighting & Ambiance' },
    { id: 'furniture-curation', label: 'Furniture & Linen' },
    { id: 'tablescapes', label: 'Detailed Tablescapes' },
    { id: 'custom-installations', label: 'Custom Backdrops / Installations' },
];

const DecorQuoteModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        eventType: '',
        eventDate: '',
        venue: '',
        styles: [],
        elements: [],
        message: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleCheckboxToggle = (field, value) => {
        setFormData(prev => {
            const currentValues = prev[field];
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            return { ...prev, [field]: newValues };
        });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Your name is required';
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'A valid email is required';
        if (!formData.eventType) newErrors.eventType = 'Please select an event type';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);

        const whatsappNumber = "+254727937010";
        const eventTypeLabel = eventTypeOptions.find(opt => opt.value === formData.eventType)?.label;
        const selectedStyles = formData.styles.map(sId => stylePreferences.find(s => s.id === sId)?.label).join(', ');
        const selectedElements = formData.elements.map(eId => decorElements.find(e => e.id === eId)?.label).join(', ');

        let message = `*🎨 Event Decor & Styling Inquiry 🎨*\n\n`;
        message += `*Client Name:* ${formData.name}\n`;
        message += `*Phone:* ${formData.phone}\n`;
        message += `*Email:* ${formData.email}\n\n`;
        message += `*Event Overview:*\n`;
        message += `  - *Event Type:* ${eventTypeLabel}\n`;
        if(formData.eventDate) message += `  - *Proposed Date:* ${formData.eventDate}\n`;
        if(formData.venue) message += `  - *Venue/Location:* ${formData.venue}\n`;
        if(selectedStyles) message += `\n*Preferred Styles:*\n${selectedStyles}\n`;
        if(selectedElements) message += `\n*Key Decor Elements:*\n${selectedElements}\n`;
        if (formData.message) message += `\n*Vision & Ideas:*\n${formData.message}`;

        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        
        setIsSubmitting(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="font-serif font-bold text-2xl text-gray-900">Request a Design Consultation</h2>
                            <p className="font-sans text-gray-500 mt-1">Let's start bringing your vision to life.</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={onClose} className="-mt-2 -mr-2">
                            <Icon name="X" size={24} />
                        </Button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input label="Full Name" required value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)} error={errors.name} />
                        <Input label="Phone Number" type="tel" required value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} error={errors.phone} />
                    </div>
                    <div>
                        <Input label="Email Address" type="email" required value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} error={errors.email} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Select label="Type of Event" required options={eventTypeOptions} value={formData.eventType} onChange={(value) => handleInputChange('eventType', value)} error={errors.eventType} />
                        <Input label="Event Date (Optional)" type="date" value={formData.eventDate} onChange={(e) => handleInputChange('eventDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
                    </div>
                     <div>
                        <Input label="Venue / Location (if known)" type="text" value={formData.venue} onChange={(e) => handleInputChange('venue', e.target.value)} />
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-3 block">What is your preferred style?</label>
                        <div className="grid grid-cols-2 gap-3">
                            {stylePreferences.map((style) => ( <Checkbox key={style.id} label={style.label} checked={formData.styles.includes(style.id)} onChange={() => handleCheckboxToggle('styles', style.id)} /> ))}
                        </div>
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-3 block">Which decor elements are most important to you?</label>
                        <div className="grid grid-cols-2 gap-3">
                            {decorElements.map((elem) => ( <Checkbox key={elem.id} label={elem.label} checked={formData.elements.includes(elem.id)} onChange={() => handleCheckboxToggle('elements', elem.id)} /> ))}
                        </div>
                    </div>
                    <div>
                        <label className="font-sans font-medium text-gray-800 mb-2 block">Tell us about your vision (Optional)</label>
                        <textarea value={formData.message} onChange={(e) => handleInputChange('message', e.target.value)} placeholder="Describe your dream event, inspiration, or any questions you have..." rows={3} className="w-full p-3 border border-gray-300 rounded-lg font-sans bg-white focus:outline-none focus:ring-2 focus:ring-primary" />
                    </div>
                    <div className="pt-4 border-t border-gray-200">
                        <Button type="submit" variant="primary" loading={isSubmitting} fullWidth size="lg">
                            {isSubmitting ? 'Submitting...' : 'Send Consultation Request'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DecorQuoteModal;